import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  PageBreak,
  Header,
  Footer,
  PageNumber,
  NumberFormat,
  convertMillimetersToTwip,
} from 'docx';
import { saveAs } from 'file-saver';
import type { ReportData, InlineRun } from './types';
import { htmlToBlocks } from './htmlToBlocks';

function formatDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_').replace(/_+/g, '_');
}

// Same character normalization as PDF so both outputs are identical
function normalizeText(text: string): string {
  return text
    .replace(/[₀₁₂₃₄₅₆₇₈₉]/g, c => '0123456789'['₀₁₂₃₄₅₆₇₈₉'.indexOf(c)])
    .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹]/g, c => '0123456789'['⁰¹²³⁴⁵⁶⁷⁸⁹'.indexOf(c)])
    .replace(/→/g, '->')
    .replace(/←/g, '<-')
    .replace(/↑/g, '^')
    .replace(/↓/g, 'v');
}

// Split on \n (from <br> tags) and insert Word soft line-breaks between parts
function runsToTextRuns(runs: InlineRun[]): TextRun[] {
  const result: TextRun[] = [];
  for (const run of runs) {
    const parts = normalizeText(run.text).split('\n');
    for (let i = 0; i < parts.length; i++) {
      const opts = {
        bold: run.bold,
        italics: run.italic,
        underline: run.underline ? {} : undefined,
        size: 22 as const,
        font: 'Calibri',
      };
      if (i === 0) {
        if (parts[i]) result.push(new TextRun({ text: parts[i], ...opts }));
      } else {
        // break: 1 inserts a soft line-break before this text
        result.push(new TextRun({ text: parts[i] || '', break: 1, ...opts }));
      }
    }
  }
  return result;
}

function createEmptyParagraph(): Paragraph {
  return new Paragraph({ spacing: { after: 100 } });
}

export async function exportDocx(data: ReportData): Promise<void> {
  const children: (Paragraph | Table)[] = [];

  // === TITLE PAGE ===
  children.push(new Paragraph({ spacing: { before: 4000 } }));
  children.push(new Paragraph({
    alignment: AlignmentType.CENTER,
    children: [new TextRun({
      text: data.metadata.berichtstitel || 'BAFA Beratungsbericht',
      bold: true,
      size: 48, // 24pt
      font: 'Calibri',
    })],
  }));
  children.push(createEmptyParagraph());

  if (data.company.name) {
    children.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({
        text: data.company.name,
        size: 32, // 16pt
        font: 'Calibri',
      })],
    }));
  }

  children.push(createEmptyParagraph());
  children.push(createEmptyParagraph());

  // Metadata block
  const metaLines: string[] = [];
  if (data.metadata.aktenzeichen) metaLines.push(`Vorgangsnummer: ${data.metadata.aktenzeichen}`);
  if (data.consultants.length > 0) {
    metaLines.push(`Berater: ${data.consultants.map(c => c.name).join(', ')}`);
  }
  if (data.advisorNumber) {
    metaLines.push(`BAFA-Beraternummer: ${data.advisorNumber}`);
  }

  for (const line of metaLines) {
    children.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text: line, size: 22, font: 'Calibri' })],
    }));
  }

  // Page break after title
  children.push(new Paragraph({
    children: [new PageBreak()],
  }));

  // === SECTIONS ===
  for (const section of data.sections) {
    // Section heading
    children.push(new Paragraph({
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 400, after: 200 },
      children: [new TextRun({
        text: section.title,
        bold: true,
        size: 28, // 14pt
        font: 'Calibri',
      })],
    }));

    if (!section.htmlContent || !section.htmlContent.trim()) {
      children.push(new Paragraph({
        children: [new TextRun({
          text: '(Kein Inhalt)',
          italics: true,
          size: 22,
          font: 'Calibri',
          color: '999999',
        })],
      }));
      children.push(createEmptyParagraph());
      continue;
    }

    // Parse HTML to blocks and render
    let blocks = htmlToBlocks(section.htmlContent);
    // Remove duplicate heading if the HTML content starts with the same section title
    if (blocks.length > 0 && blocks[0].type === 'heading') {
      const firstHeadingText = blocks[0].text.trim().toLowerCase();
      const sectionTitle = section.title.trim().toLowerCase();
      if (firstHeadingText === sectionTitle ||
          (firstHeadingText.startsWith(`${section.number}.`) && sectionTitle.startsWith(`${section.number}.`))) {
        blocks = blocks.slice(1);
      }
    }

    for (const block of blocks) {
      switch (block.type) {
        case 'heading': {
          const level = block.level === 1 ? HeadingLevel.HEADING_2 :
                        block.level === 2 ? HeadingLevel.HEADING_3 :
                        HeadingLevel.HEADING_4;
          children.push(new Paragraph({
            heading: level,
            spacing: { before: 200, after: 100 },
            children: [new TextRun({
              text: block.text,
              bold: true,
              size: block.level === 1 ? 26 : block.level === 2 ? 24 : 22,
              font: 'Calibri',
            })],
          }));
          break;
        }

        case 'paragraph': {
          children.push(new Paragraph({
            spacing: { after: 120 },
            children: runsToTextRuns(block.runs),
          }));
          break;
        }

        case 'list': {
          block.items.forEach((item, idx) => {
            const bullet = block.ordered ? `${idx + 1}. ` : '- ';
            children.push(new Paragraph({
              spacing: { after: 60 },
              indent: { left: convertMillimetersToTwip(10) },
              children: [
                new TextRun({ text: bullet, size: 22, font: 'Calibri' }),
                ...runsToTextRuns(item),
              ],
            }));
          });
          break;
        }

        case 'table': {
          if (block.rows.length === 0) break;
          const colCount = Math.max(...block.rows.map(r => r.length));

          const tableRows = block.rows.map((row, rowIdx) =>
            new TableRow({
              children: Array.from({ length: colCount }, (_, colIdx) =>
                new TableCell({
                  width: { size: Math.floor(100 / colCount), type: WidthType.PERCENTAGE },
                  children: [new Paragraph({
                    children: [new TextRun({
                      text: row[colIdx] || '',
                      bold: rowIdx === 0,
                      size: 20,
                      font: 'Calibri',
                    })],
                  })],
                })
              ),
            })
          );

          children.push(new Table({
            rows: tableRows,
            width: { size: 100, type: WidthType.PERCENTAGE },
          }));
          children.push(createEmptyParagraph());
          break;
        }
      }
    }

    children.push(createEmptyParagraph());
  }

  // === BUILD DOCUMENT ===
  const az = data.metadata.aktenzeichen || '';
  const doc = new Document({
    sections: [{
      properties: {
        page: {
          margin: {
            top: convertMillimetersToTwip(25),
            bottom: convertMillimetersToTwip(25),
            left: convertMillimetersToTwip(25),
            right: convertMillimetersToTwip(25),
          },
          size: {
            width: convertMillimetersToTwip(210),
            height: convertMillimetersToTwip(297),
          },
          pageNumbers: { start: 1 },
        },
      },
      headers: {
        default: new Header({
          children: [new Paragraph({
            children: [new TextRun({
              text: `BAFA Beratungsbericht${az ? ' - ' + az : ''}`,
              size: 16,
              font: 'Calibri',
              color: '999999',
            })],
          })],
        }),
      },
      footers: {
        default: new Footer({
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new TextRun({ children: ['Seite ', PageNumber.CURRENT, ' von ', PageNumber.TOTAL_PAGES], size: 16, font: 'Calibri', color: '999999' }),
            ],
          })],
        }),
      },
      children,
    }],
  });

  const blob = await Packer.toBlob(doc);
  const safeName = az ? sanitizeFilename(az) : 'Bericht';
  const today = new Date().toISOString().slice(0, 10);
  saveAs(blob, `BAFA_Beratungsbericht_${safeName}_${today}.docx`);
}
